import { getCollageTemplate, getGradientPreset } from '@/content/collageTemplates'
import {
  CollageBackgroundKind,
  collageCellRect,
  type CollageNode,
  type CompositionNode,
  type SourceId,
} from '@/core/document'
import type { Size } from '@/core/geometry'
import { RenderUnsupportedError } from '@/core/render/errors'
import type { NodeRenderSource, RenderContext } from '@/core/render/renderNode'

type RenderCollageChild = (
  child: CompositionNode,
  targetSize: Size,
  context: RenderContext,
) => void

function gradientLine(size: Size, angleDeg: number) {
  const radians = angleDeg * Math.PI / 180
  const vector = { x: Math.sin(radians), y: -Math.cos(radians) }
  const distance = Math.abs(vector.x) * size.width / 2 + Math.abs(vector.y) * size.height / 2
  const center = { x: size.width / 2, y: size.height / 2 }
  return {
    start: { x: center.x - vector.x * distance, y: center.y - vector.y * distance },
    end: { x: center.x + vector.x * distance, y: center.y + vector.y * distance },
  }
}

function drawCoverImage(
  source: NodeRenderSource,
  targetSize: Size,
  context: RenderContext,
): void {
  const scale = Math.max(targetSize.width / source.width, targetSize.height / source.height)
  const width = source.width * scale
  const height = source.height * scale
  context.drawImage(
    source.bitmap,
    (targetSize.width - width) / 2,
    (targetSize.height - height) / 2,
    width,
    height,
  )
}

function drawBackground(
  node: CollageNode,
  targetSize: Size,
  sources: ReadonlyMap<SourceId, NodeRenderSource>,
  context: RenderContext,
): void {
  switch (node.background.kind) {
    case CollageBackgroundKind.Color:
      context.fillStyle = node.background.color
      context.fillRect(0, 0, targetSize.width, targetSize.height)
      return
    case CollageBackgroundKind.Gradient: {
      const preset = getGradientPreset(node.background.presetId)
      const line = gradientLine(targetSize, preset.angleDeg)
      const gradient = context.createLinearGradient(line.start.x, line.start.y, line.end.x, line.end.y)
      gradient.addColorStop(0, node.background.colors[0])
      gradient.addColorStop(1, node.background.colors[1])
      context.fillStyle = gradient
      context.fillRect(0, 0, targetSize.width, targetSize.height)
      return
    }
    case CollageBackgroundKind.Image: {
      const source = sources.get(node.background.sourceId)
      if (!source) {
        throw new Error(`Collage "${node.id}" background references missing source "${node.background.sourceId}"`)
      }
      context.save()
      if (node.background.blur > 0) {
        if (!('filter' in context)) throw new RenderUnsupportedError('Canvas 2D filters')
        const filter = `blur(${node.background.blur}px)`
        context.filter = filter
        if (context.filter !== filter) throw new RenderUnsupportedError('Canvas 2D filters')
      }
      drawCoverImage(source, targetSize, context)
      context.restore()
      return
    }
    default:
      throw new Error(`Collage "${node.id}" has unknown background kind "${String((node.background as { kind: unknown }).kind)}"`)
  }
}

export function renderCollage(
  node: CollageNode,
  targetSize: Size,
  sources: ReadonlyMap<SourceId, NodeRenderSource>,
  context: RenderContext,
  renderChild: RenderCollageChild,
): void {
  const template = getCollageTemplate(node.templateId)
  drawBackground(node, targetSize, sources, context)
  const orderedCells = template.cells
    .map((cell, index) => ({ cell, index, child: node.cells[index] ?? null }))
    .sort((first, second) => first.cell.z - second.cell.z)
  for (const { cell, child } of orderedCells) {
    if (!child) continue
    const rect = collageCellRect(template, cell, targetSize, node.gap)
    context.save()
    context.translate(rect.x + rect.width / 2, rect.y + rect.height / 2)
    context.rotate(cell.rotationDeg * Math.PI / 180)
    context.translate(-rect.width / 2, -rect.height / 2)
    context.beginPath()
    context.rect(0, 0, rect.width, rect.height)
    context.clip()
    renderChild(child, { width: rect.width, height: rect.height }, context)
    context.restore()
  }
}
